/**
 * Renata OS — Missions (/app/renata/missions)
 * List + detail: progress, checklist, tasks, insights link, activity.
 */
import { RenataPageChrome } from "@/components/renata/RenataPageChrome";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, useCallback } from "react";
import {
  Target, Plus, RefreshCw, ChevronRight, CheckCircle2,
  Clock, XCircle, AlertTriangle, Zap, Lightbulb, Activity,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { clientRequestHeaders } from "@/lib/sm-api";
import { toast } from "sonner";
import { localizeEventKey } from "@/lib/renata/experience/events";
import type { MissionInstance, MissionEvent, MissionStatus, MissionTemplateId } from "@/lib/renata/missions/types";
import { listMissionTemplates } from "@/lib/renata/missions/registry";
import {
  Dialog, DialogContent,
  DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/app/renata/missions")({
  component: RenataMissionsPage,
  head: () => ({ meta: [{ title: "Planes de trabajo — Renata — Structura Market" }] }),
});

const STATUS_CONFIG: Record<MissionStatus, {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  className: string;
}> = {
  pending:   { label: "Pendiente",   icon: Clock,         className: "text-slate-500 border-slate-200 bg-slate-50 dark:bg-slate-900/30 dark:border-slate-700" },
  active:    { label: "En curso",      icon: Target,        className: "text-indigo-600 border-indigo-200 bg-indigo-50 dark:bg-indigo-950/30 dark:border-indigo-800" },
  paused:    { label: "Pausada",     icon: AlertTriangle, className: "text-amber-600 border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800" },
  completed: { label: "Completada",  icon: CheckCircle2,  className: "text-emerald-600 border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800" },
  abandoned: { label: "Abandonada",  icon: XCircle,       className: "text-red-600 border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-800" },
};

function MissionCard({
  mission,
  selected,
  onSelect,
}: {
  mission: MissionInstance;
  selected: boolean;
  onSelect: () => void;
}) {
  const cfg = STATUS_CONFIG[mission.status];
  const Icon = cfg.icon;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "w-full text-left rounded-xl border p-4 space-y-2 transition-colors",
        cfg.className,
        selected && "ring-2 ring-indigo-400",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <Icon className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{mission.title}</p>
            <p className="text-xs text-muted-foreground">
              {new Date(mission.created_at).toLocaleDateString("es-AR")}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Badge variant="outline" className="text-[10px]">{cfg.label}</Badge>
          <ChevronRight className={cn("h-3.5 w-3.5 transition-transform text-muted-foreground", selected && "rotate-90")} />
        </div>
      </div>
      <Progress value={mission.progress} className="h-1.5" />
      <p className="text-xs text-muted-foreground">{mission.progress}% completado · {mission.task_ids.length} tarea{mission.task_ids.length !== 1 ? "s" : ""}</p>
    </button>
  );
}

function MissionDetail({
  mission,
  events,
  onStatusChange,
}: {
  mission: MissionInstance;
  events: MissionEvent[];
  onStatusChange: (status: MissionStatus) => void;
}) {
  const cfg = STATUS_CONFIG[mission.status];
  const canActivate = mission.status === "pending" || mission.status === "paused";
  const canComplete = mission.status === "active";
  const canAbandon = mission.status === "active" || mission.status === "paused";

  return (
    <div className="space-y-4 p-4">
      {/* Header */}
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold">{mission.title}</h2>
          <Badge variant="outline" className={cn("text-[10px]", cfg.className)}>{cfg.label}</Badge>
        </div>
        {mission.description && (
          <p className="text-sm text-muted-foreground">{mission.description}</p>
        )}
      </div>

      {/* Progress */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Progreso</span>
          <span className="font-medium">{mission.progress}%</span>
        </div>
        <Progress value={mission.progress} className="h-2" />
      </div>

      {/* Task checklist */}
      {mission.task_ids.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Tareas ({mission.task_ids.length})
          </p>
          <div className="space-y-1">
            {mission.task_ids.map((tid, i) => (
              <div key={tid} className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-xs">
                <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center text-[10px] font-medium">
                  {i + 1}
                </div>
                <span className="truncate font-mono text-[11px] text-muted-foreground">{tid.slice(0, 8)}…</span>
                <Link to="/app/renata/tasks" className="ml-auto">
                  <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1 px-2">
                    <Zap className="h-2.5 w-2.5" /> Ver
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {canActivate && (
          <Button size="sm" className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700 gap-1" onClick={() => onStatusChange("active")}>
            <Target className="h-3 w-3" /> Activar
          </Button>
        )}
        {canComplete && (
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => onStatusChange("completed")}>
            <CheckCircle2 className="h-3 w-3" /> Completar
          </Button>
        )}
        {canAbandon && (
          <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => onStatusChange("abandoned")}>
            <XCircle className="h-3 w-3" /> Abandonar
          </Button>
        )}
        <Link to="/app/renata/insights">
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1">
            <Lightbulb className="h-3 w-3" /> Oportunidades
          </Button>
        </Link>
      </div>

      {/* Outcome */}
      {mission.outcome && (
        <div className="rounded-lg bg-muted p-3 text-xs">
          <p className="font-medium mb-1">Resultado</p>
          <p className="text-muted-foreground">{mission.outcome}</p>
        </div>
      )}

      {/* Activity */}
      {events.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Actividad
          </p>
          <div className="space-y-1.5">
            {events.slice(0, 8).map((ev) => (
              <div key={ev.id} className="flex items-start gap-2 text-xs">
                <Activity className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
                <div>
                  <span className="font-medium text-foreground">{localizeEventKey(ev.event_type)}</span>
                  <span className="ml-1.5 text-foreground/70">
                    {new Date(ev.created_at).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function NewMissionDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: () => void;
}) {
  const tenantId = useWorkspaceTenantId();
  const templates = listMissionTemplates();
  const [templateId, setTemplateId] = useState<string>(templates[0]?.id ?? "");
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);

  const selectedTemplate = templates.find((t) => t.id === templateId);

  async function handleCreate() {
    if (!tenantId || !templateId) return;
    setSaving(true);
    try {
      const res = await fetch("/api/renata/missions", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json", ...clientRequestHeaders(tenantId) },
        body: JSON.stringify({ templateId, title: title.trim() || undefined }),
      });
      if (!res.ok) throw new Error("Error al crear misión");
      toast.success("Plan de trabajo creado. Revisá los pasos y activá cuando quieras avanzar.");
      onOpenChange(false);
      onCreated();
    } catch {
      toast.error("No se pudo crear el plan de trabajo");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nuevo plan de trabajo</DialogTitle>
          <DialogDescription>
            Elegí un tipo de plan. Se crea en estado pendiente: vos lo activás y aprobás los pasos.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Tipo de plan</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Elegí un tipo de plan…" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedTemplate && (
              <p className="text-xs text-muted-foreground">{selectedTemplate.description}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Título personalizado (opcional)</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={selectedTemplate?.title ?? "Título de la misión…"}
              className="h-9"
            />
          </div>
          {selectedTemplate && (
            <div className="rounded-lg bg-muted p-3 text-xs space-y-1">
              <p className="font-medium">Tareas incluidas ({selectedTemplate.taskIds.length})</p>
              <div className="flex flex-wrap gap-1">
                {selectedTemplate.taskIds.map((tid) => (
                  <Badge key={tid} variant="outline" className="text-[10px]">{tid.replace(/_/g, " ")}</Badge>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={handleCreate}
            disabled={saving || !templateId}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {saving ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : "Crear misión"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RenataMissionsPage() {
  const tenantId = useWorkspaceTenantId();
  const [missions, setMissions] = useState<MissionInstance[]>([]);
  const [selected, setSelected] = useState<MissionInstance | null>(null);
  const [events, setEvents] = useState<MissionEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await fetch("/api/renata/missions", {
        credentials: "include",
        headers: clientRequestHeaders(tenantId),
      });
      const data = await res.json() as { missions: MissionInstance[] };
      setMissions(data.missions ?? []);
    } catch {
      toast.error("Error al cargar planes de trabajo");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { void load(); }, [load]);

  async function loadEvents(missionId: string) {
    if (!tenantId) return;
    try {
      const res = await fetch(`/api/renata/missions?id=${encodeURIComponent(missionId)}`, {
        credentials: "include",
        headers: clientRequestHeaders(tenantId),
      });
      if (res.ok) {
        const data = await res.json() as { events?: MissionEvent[] };
        setEvents(data.events ?? []);
      }
    } catch { /* skip */ }
  }

  function handleSelect(m: MissionInstance) {
    setSelected(m);
    void loadEvents(m.id);
  }

  async function handleStatusChange(status: MissionStatus) {
    if (!selected || !tenantId) return;
    try {
      const res = await fetch("/api/renata/missions", {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json", ...clientRequestHeaders(tenantId) },
        body: JSON.stringify({ id: selected.id, status }),
      });
      if (!res.ok) throw new Error("Error");
      toast.success(`Plan de trabajo ${status === "completed" ? "completado" : status === "abandoned" ? "abandonado" : "actualizado"}`);
      void load().then(() => {
        const updated = missions.find((m) => m.id === selected.id);
        if (updated) setSelected({ ...updated, status });
      });
    } catch {
      toast.error("No se pudo actualizar el plan");
    }
  }

  return (
    <div className="flex min-w-0 flex-col gap-4 lg:flex-row lg:items-start">
      {/* List */}
      <div className="flex w-full shrink-0 flex-col border-b min-w-0 lg:w-72 lg:border-b-0 lg:border-r">
        <div className="border-b px-4 py-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <h1 className="text-sm font-semibold flex items-center gap-2">
              <Target className="h-4 w-4 text-indigo-500" />
              Planes de trabajo
            </h1>
            <div className="flex gap-1">
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={load}>
                <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
              </Button>
              <Button size="sm" className="h-7 text-xs gap-1 bg-indigo-600 hover:bg-indigo-700" onClick={() => setShowNew(true)}>
                <Plus className="h-3 w-3" /> Nuevo
              </Button>
            </div>
          </div>
          <p className="text-[11px] text-foreground/80 leading-snug">
            Un plan agrupa pasos. <strong>Activar</strong> lo pone en curso (no ejecuta solo). Cada cambio sensible pide tu OK.
          </p>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-2">
          {loading && (
            <div className="flex h-24 items-center justify-center">
              <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}
          {!loading && missions.length === 0 && (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <Target className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Sin misiones todavía.</p>
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 gap-1" onClick={() => setShowNew(true)}>
                <Plus className="h-3.5 w-3.5" /> Crear primera misión
              </Button>
            </div>
          )}
          {missions.map((m) => (
            <MissionCard
              key={m.id}
              mission={m}
              selected={selected?.id === m.id}
              onSelect={() => handleSelect(m)}
            />
          ))}
        </div>
      </div>

      {/* Detail */}
      <div className="flex-1 overflow-y-auto">
        {selected ? (
          <MissionDetail
            mission={selected}
            events={events}
            onStatusChange={handleStatusChange}
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <div className="text-center space-y-2">
              <Target className="h-10 w-10 text-muted-foreground mx-auto" />
              <p className="text-sm text-muted-foreground">
                Seleccioná una misión para ver el detalle
              </p>
            </div>
          </div>
        )}
      </div>

      <NewMissionDialog
        open={showNew}
        onOpenChange={setShowNew}
        onCreated={() => void load()}
      />
    </div>
  );
}
